import { apiClient } from "../config/axiosConfig";
import { CryptoUtils } from "../utils/crypto-utils";

interface CryptoInitResponse {
  success: boolean;
  public_key: string;
}

interface KeyExchangeResponse {
  success: boolean;
  key_id: string;
}

// Interface for encrypted data structure (currently unused but kept for type safety)
// interface EncryptedData {
//   encrypted: {
//     key_id: string;
//     iv: string;
//     data: string;
//   };
// }

export class KeyMismatchError extends Error {
  readonly expectedKeyId: string | null;
  readonly responseKeyId: string;

  constructor(
    expectedKeyId: string | null,
    responseKeyId: string,
    message?: string
  ) {
    super(
      message ??
        `Expected keyId=${expectedKeyId ?? "undefined"}, got=${responseKeyId}`
    );
    this.name = "KeyMismatchError";
    this.expectedKeyId = expectedKeyId;
    this.responseKeyId = responseKeyId;
  }
}

export const STORAGE_KEYS = {
  JWT_TOKEN: "jwt_token",
  SYMMETRIC_KEY: "symmetric_key",
  KEY_ID: "key_id",
  PUBLIC_KEY: "public_key",
  EPIC_INTROSPECT_URL: "epic_introspect_url",
  EXPIRES_IN: "expires_in",
  REFRESH_TOKEN: "refresh_token",
  REFRESH_EXPIRES_IN: "refresh_expires_in",
} as const;

export class EncryptionService {
  private symmetricKey: CryptoKey | null = null;
  private keyId: string | null = null;
  private publicKey: string | null = null;
  private baseUrl: string;

  // Add key loading state management
  private keyLoadingPromise: Promise<boolean> | null = null;
  private isKeyLoading = false;
  private lastKeyLoadTime = 0;
  private keyLoadTimeout = 5000; // 5 second timeout for key loading

  // Memory cache for frequently accessed values
  private static memoryCache = new Map<string, string>();

  constructor(baseUrl: string = "") {
    this.baseUrl = process.env.REACT_APP_BASE_URL || baseUrl;
  }

  // Initialize encryption: get public key + exchange symmetric key
  async initializeEncryption(): Promise<void> {
    try {
      const token = this.getJwtToken();

      // Step 1: get public key
      const res = await fetch(`${this.baseUrl}crypto/init`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!res.ok) throw new Error(`Failed to get public key: ${res.status}`);

      const data: CryptoInitResponse = await res.json();
      if (!data.success || !data.public_key)
        throw new Error("Invalid public key response");

      this.publicKey = data.public_key;
      localStorage.setItem(STORAGE_KEYS.PUBLIC_KEY, this.publicKey);

      // Step 2: generate and exchange symmetric key
      await this.generateAndExchangeKey();
    } catch (err) {
      console.error("[ENCRYPTION] Failed to initialize:", err);
      throw err;
    }
  }

  private async generateAndExchangeKey(): Promise<void> {
    if (!this.publicKey) throw new Error("Public key not available");

    const symmetricKey = await window.crypto.subtle.generateKey(
      { name: "AES-CBC", length: 256 },
      true,
      ["encrypt", "decrypt"]
    );

    const rawKey = await window.crypto.subtle.exportKey("raw", symmetricKey);
    const pemKey = CryptoUtils.base64ToPem(this.publicKey);
    const publicCryptoKey = await window.crypto.subtle.importKey(
      "spki",
      CryptoUtils.pemToArrayBuffer(pemKey),
      { name: "RSA-OAEP", hash: "SHA-256" },
      false,
      ["encrypt"]
    );

    const encryptedKey = await window.crypto.subtle.encrypt(
      { name: "RSA-OAEP" },
      publicCryptoKey,
      rawKey
    );
    const base64EncryptedKey = CryptoUtils.arrayBufferToBase64(encryptedKey);

    const token = this.getJwtToken();
    const res = await fetch(`${this.baseUrl}crypto/key-exchange`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ encrypted_symmetric_key: base64EncryptedKey }),
    });

    if (!res.ok) throw new Error(`Key exchange failed: ${res.status}`);

    const keyData: KeyExchangeResponse = await res.json();
    if (!keyData.success || !keyData.key_id)
      throw new Error("Invalid key exchange response");

    this.symmetricKey = symmetricKey;
    this.keyId = keyData.key_id;

    // Persist symmetric key and keyId
    const keyBase64 = CryptoUtils.arrayBufferToBase64(rawKey);
    localStorage.setItem(STORAGE_KEYS.SYMMETRIC_KEY, keyBase64);
    localStorage.setItem(STORAGE_KEYS.KEY_ID, this.keyId);
  }

  // Load symmetric key from localStorage if exists
  async loadSymmetricKey(): Promise<boolean> {
    try {
      const keyBase64 = localStorage.getItem(STORAGE_KEYS.SYMMETRIC_KEY);
      const keyId = localStorage.getItem(STORAGE_KEYS.KEY_ID);

      if (!keyBase64 || !keyId) return false;

      // Check if key ID changed (server rotated keys)
      if (this.keyId && this.keyId !== keyId) {
        console.log("Key ID changed, clearing old keys");
        this.symmetricKey = null;
        this.keyId = null;
      }

      const keyData = CryptoUtils.base64ToArrayBuffer(keyBase64);

      // Add timeout to key import operation
      const importPromise = window.crypto.subtle.importKey(
        "raw",
        keyData,
        { name: "AES-CBC" },
        true,
        ["encrypt", "decrypt"]
      );

      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(
          () => reject(new Error("Key import timeout")),
          this.keyLoadTimeout
        );
      });

      this.symmetricKey = await Promise.race([importPromise, timeoutPromise]);
      this.keyId = keyId;
      return true;
    } catch (err) {
      console.error("[ENCRYPTION] Failed to load symmetric key:", err);
      return false;
    }
  }

  // Decrypt response data
  async decryptResponse(encryptedResponse: any): Promise<any> {
    if (!encryptedResponse?.encrypted) return encryptedResponse;

    if (!this.symmetricKey) {
      const loaded = await this.loadSymmetricKey();
      if (!loaded)
        throw new Error("Symmetric key not available for decryption");
    }

    const { key_id, iv, data } = encryptedResponse.encrypted;

    if (key_id !== this.keyId) {
      const storedKeyId = localStorage.getItem(STORAGE_KEYS.KEY_ID);

      // In-memory key is stale; localStorage matches the response key.
      if (key_id === storedKeyId) {
        this.symmetricKey = null;
        this.keyId = null;
        const loaded = await this.loadSymmetricKey();
        if (!loaded || this.keyId !== key_id) {
          throw new KeyMismatchError(this.keyId, key_id);
        }
      } else {
        throw new KeyMismatchError(this.keyId, key_id);
      }
    }

    const ivBytes = CryptoUtils.base64ToArrayBuffer(iv);
    const encryptedBytes = CryptoUtils.base64ToArrayBuffer(data);

    const decryptedPaddedBytes = await CryptoUtils.decryptWithAES(
      this.symmetricKey!,
      ivBytes,
      encryptedBytes
    );
    const decryptedBytes = CryptoUtils.unpadData(decryptedPaddedBytes);
    return JSON.parse(new TextDecoder().decode(decryptedBytes));
  }

  reset(): void {
    this.symmetricKey = null;
    this.keyId = null;
    this.publicKey = null;
    this.isKeyLoading = false;
    this.keyLoadingPromise = null;
    this.lastKeyLoadTime = 0;

    localStorage.removeItem(STORAGE_KEYS.SYMMETRIC_KEY);
    localStorage.removeItem(STORAGE_KEYS.KEY_ID);
    localStorage.removeItem(STORAGE_KEYS.PUBLIC_KEY);
    // localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    // localStorage.removeItem(STORAGE_KEYS.REFRESH_EXPIRES_IN);
  }

  isReady(): boolean {
    return this.symmetricKey !== null && this.keyId !== null;
  }

  setJwtToken(token: string) {
    localStorage.setItem(STORAGE_KEYS.JWT_TOKEN, token);
  }

  getJwtToken(): string | null {
    return localStorage.getItem(STORAGE_KEYS.JWT_TOKEN);
  }

  setRefreshToken(token: string) {
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, token);
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
  }

  setRefreshExpiresIn(expiresIn: string) {
    localStorage.setItem(STORAGE_KEYS.REFRESH_EXPIRES_IN, expiresIn);
  }

  getRefreshExpiresIn(): string | null {
    return localStorage.getItem(STORAGE_KEYS.REFRESH_EXPIRES_IN);
  }

  // Refresh access token using refresh token
  async refreshAccessToken(): Promise<void> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      throw new Error("No refresh token available");
    }

    // Check if refresh token is expired
    const refreshExpiresIn = this.getRefreshExpiresIn();
    if (refreshExpiresIn) {
      const expiresAt = parseInt(refreshExpiresIn) * 1000; // Convert to milliseconds
      if (Date.now() >= expiresAt) {
        console.log("[REFRESH TOKEN] Refresh token has expired");
        this.clearTokens();
        throw new Error("Refresh token has expired");
      }
    }

    try {
      const response = await apiClient.post("crypto/auth/refresh-token", {
        refresh_token: refreshToken,
      });

      const data = response.data;

      if (data.success && data.data?.access_token) {
        this.setJwtToken(data.data.access_token);

        if (data.data.expires_in) {
          localStorage.setItem(
            STORAGE_KEYS.EXPIRES_IN,
            String(data.data.expires_in)
          );
        }

        if (data.data.refresh_token) {
          this.setRefreshToken(data.data.refresh_token);
        }

        if (data.data.refresh_expires_in) {
          this.setRefreshExpiresIn(String(data.data.refresh_expires_in));
        }
      } else {
        throw new Error("Invalid refresh token response");
      }
    } catch (err: any) {
      console.error("[REFRESH TOKEN] Failed:", err);
      
      // If refresh token is invalid/expired, clear all tokens
      if (err.response?.status === 401 || err.response?.status === 403) {
        console.log("[REFRESH TOKEN] Refresh token invalid, clearing tokens");
        this.clearTokens();
      }
      
      throw new Error(
        `Token refresh failed: ${err.response?.status || err.message}`
      );
    }
  }

  // Clear all tokens (useful when refresh fails)
  clearTokens(): void {
    localStorage.removeItem(STORAGE_KEYS.JWT_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_EXPIRES_IN);
    localStorage.removeItem(STORAGE_KEYS.EXPIRES_IN);
  }

  // Add method to check if key loading is in progress
  isKeyLoadingInProgress(): boolean {
    return this.isKeyLoading || this.keyLoadingPromise !== null;
  }

  // Add method to wait for key loading to complete
  async waitForKeyLoading(): Promise<void> {
    if (this.keyLoadingPromise) {
      await this.keyLoadingPromise;
    }
  }
}
